import { isDeepEqual } from "@via/utils/helpers.tsx";
import { createContext, type JSX, type ReactNode, useState } from "react";

interface Props {
  children?: ReactNode;
}

export interface Sample {
  id?: string;
  name: string;
  filesUsed: string;
  directory: string;
  file1?: string;
  file2?: string;
  file3?: string;
  file4?: string;
}

export interface IFileOrganizerContext {
  selectedSamples: Sample[] | null;
  setSelectedSamples: (newList: Sample[]) => void;
  updateSampleName: (originalSample: Sample, newName: string) => void;
  addNewSamples: (newSamples: Sample[]) => void;
  removeSample: (id: string) => void;
  resetSampleList: () => void;
  resetTrigger: number;
  triggerFullReset: () => void;
}

const initial = {
  selectedSamples: [],
  setSelectedSamples: (): void => {},
  updateSampleName: (): void => {},
  addNewSamples: (): void => {},
  removeSample: (): void => {},
  resetSampleList: (): void => {},
  resetTrigger: 0,
  triggerFullReset: (): void => {},
};

const FileOrganizerContext = createContext<IFileOrganizerContext>(initial);

const FileOrganizerProvider = ({ children }: Props): JSX.Element => {
  const [selectedSamples, setSelectedSamples] = useState<Sample[] | null>(null);
  const [resetTrigger, setResetTrigger] = useState<number>(0);

  const updateSampleName = (originalSample: Sample, newName: string): void => {
    if (selectedSamples !== null) {
      const updatedSamples = selectedSamples.map(sample =>
        isDeepEqual(sample, originalSample)
          ? { ...sample, name: newName }
          : sample
      );

      setSelectedSamples(updatedSamples);
    }
  };

  const addNewSamples = (newSamples: Sample[]): void => {
    if (selectedSamples !== null) {
      setSelectedSamples([...selectedSamples, ...newSamples]);
    } else {
      setSelectedSamples(newSamples);
    }
  };

  const removeSample = (id: string): void => {
    if (selectedSamples !== null) {
      const updatedSamples = selectedSamples.filter(sample => sample.id !== id);
      setSelectedSamples(updatedSamples);
    }
  };

  const resetSampleList = (): void => {
    setSelectedSamples([]);
  };

  const triggerFullReset = (): void => {
    setResetTrigger(prev => prev + 1);
    resetSampleList();
  };

  return (
    <FileOrganizerContext.Provider
      value={{
        selectedSamples,
        setSelectedSamples,
        updateSampleName,
        addNewSamples,
        removeSample,
        resetSampleList,
        resetTrigger,
        triggerFullReset,
      }}>
      {children}
    </FileOrganizerContext.Provider>
  );
};

export { FileOrganizerContext, FileOrganizerProvider };
